/**
 * Flutter InAppWebView Bridge Utility
 * Handles two-way communication between finfi_admin_v2 (Vue PWA) and finfi_flutter_app wrapper.
 */

export const isFlutterApp = () => {
  return typeof window !== 'undefined' && Boolean(window.flutter_inappwebview)
}

/**
 * Initializes push notification service in Flutter wrapper.
 * Transmits the auth token and user profile to sync the unique FCM token.
 * @param {string} authToken
 * @param {object} userData
 */
export const initNotificationService = (authToken, userData) => {
  if (!isFlutterApp()) return

  try {
    const token =
      authToken ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token') ||
      ''
    let user =
      userData ||
      JSON.parse(localStorage.getItem('user') || 'null') ||
      {}

    const sysId =
      user?.systemUserId?._id ||
      user?.systemUserId ||
      localStorage.getItem('system-user-id') ||
      ''

    if (sysId) {
      user = { ...user, systemUserId: sysId }
    }

    window.flutter_inappwebview.callHandler('initNotificationService', token, user)
    console.log('📱 [FlutterBridge] Dispatched initNotificationService', {
      hasToken: Boolean(token),
      userId: user?._id,
      systemUserId: sysId,
    })
  } catch (error) {
    console.error('📱 [FlutterBridge] Failed to dispatch initNotificationService:', error)
  }
}

/**
 * Initializes live background location tracking service in Flutter wrapper.
 * Triggers permission verification and starts tracking if user is currently checked in.
 * @param {string} authToken
 */
export const initLiveLocationService = (authToken) => {
  if (!isFlutterApp()) return

  try {
    const token =
      authToken ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token') ||
      ''

    window.flutter_inappwebview.callHandler('initLiveLocationService', token)
    console.log('📱 [FlutterBridge] Dispatched initLiveLocationService')
  } catch (error) {
    console.error('📱 [FlutterBridge] Failed to dispatch initLiveLocationService:', error)
  }
}

/**
 * Notifies the Flutter background service of check-in / check-out changes.
 * Starts tracking on Clock In, stops tracking on Clock Out.
 * @param {boolean|string} status - true / 'CHECKED_IN' or false / 'CHECKED_OUT'
 */
export const notifyClockInStatus = (status) => {
  if (!isFlutterApp()) return

  try {
    const isCheckedIn = status === true || status === 'CHECKED_IN'
    window.flutter_inappwebview.callHandler('isCheckedIn', isCheckedIn)
    console.log('📱 [FlutterBridge] Dispatched isCheckedIn status:', isCheckedIn)
  } catch (error) {
    console.error('📱 [FlutterBridge] Failed to dispatch isCheckedIn:', error)
  }
}

/**
 * Registers global handler for Flutter wrapper callbacks (e.g. onResume refetch)
 * @param {Function} refetchFn
 */
export const registerResumeRefetch = (refetchFn) => {
  if (typeof window !== 'undefined' && typeof refetchFn === 'function') {
    window.refetchClockInStatus = refetchFn
  }
}

/**
 * Requests device GPS coordinates and telemetry from the Flutter wrapper.
 * Calls native handler 'sendLocationOnClock'.
 * @returns {Promise<{lat: number, lon: number, deviceName?: string, imeiNumber?: string, batteryHealth?: number, accuracy?: number}>}
 */
export const getFlutterLocation = () => {
  return new Promise((resolve, reject) => {
    if (!isFlutterApp()) {
      return reject(new Error('Flutter bridge is not available'))
    }

    window.flutter_inappwebview
      .callHandler('sendLocationOnClock')
      .then((response) => {
        let res = response
        if (Array.isArray(res)) res = res[0]
        if (typeof res === 'string') {
          try {
            res = JSON.parse(res)
          } catch (e) {
            // Keep res as string if JSON parsing fails
          }
        }
        const loc = res?.respLocation || res
        const lat = loc?.lat ?? loc?.latitude
        const lon = loc?.lon ?? loc?.longitude ?? loc?.lng

        if (lat !== null && lat !== undefined && lon !== null && lon !== undefined) {
          if (loc?.isMock) {
            const mockErr = new Error('Location is mocked')
            mockErr.name = 'MockLocation'
            return reject(mockErr)
          }

          resolve({
            lat: Number(lat),
            lon: Number(lon),
            deviceName: loc?.deviceName || 'Mobile Device',
            imeiNumber: loc?.imeiNumber || '',
            batteryHealth: typeof loc?.batteryHealth === 'number' ? loc.batteryHealth : 100,
            accuracy: typeof loc?.accuracy === 'number' ? loc.accuracy : 15
          })
        } else {
          const err = new Error('Location not available from Flutter')
          err.code = 1
          reject(err)
        }
      })
      .catch((err) => {
        console.error('📱 [FlutterBridge] Failed to fetch location from Flutter:', err)
        reject(err)
      })
  })
}

/**
 * Dispatches high-frequency time series activity to Flutter background tracking service.
 * @param {object} payload
 */
export const sendFlutterTimeSeriesActivity = (payload) => {
  if (!isFlutterApp()) return

  try {
    window.flutter_inappwebview.callHandler('timeSeriesActivity', payload)
    console.log('📱 [FlutterBridge] Dispatched timeSeriesActivity:', payload)
  } catch (error) {
    console.error('📱 [FlutterBridge] Failed to dispatch timeSeriesActivity:', error)
  }
}
